import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { signedOut } from "@/features/session/slice";
import { ChevronDown, LogOut, UserRound } from "@/shared/ui/icons";
import { selectUser } from "./legacySelectors";

export function AccountMenu({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const initials = `${user.firstName?.[0] || "A"}${user.lastName?.[0] || ""}`;

  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector<HTMLElement>("[role='menuitem']")?.focus();
    const closeOnOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) onOpenChange(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onOpenChange(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, onOpenChange]);

  const logout = () => {
    onOpenChange(false);
    dispatch(signedOut());
    navigate("/");
  };

  return (
    <div className="account-menu-anchor">
      <button
        ref={buttonRef}
        type="button"
        className="account-chip"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("shell.account.openMenu")}
      >
        <span>{initials}</span>
        <span className="account-label">{user.firstName || user.email || t("shell.account.profile")}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div ref={menuRef} className="account-menu" role="menu" aria-label={t("shell.account.menuLabel")}>
          <div className="account-menu-summary">
            <span>{initials}</span>
            <div>
              <strong>
                {[user.firstName, user.lastName].filter(Boolean).join(" ") || t("shell.account.profile")}
              </strong>
              <small>{user.email}</small>
            </div>
          </div>
          <div className="account-menu-separator" />
          <NavLink
            to="/account"
            role="menuitem"
            className="account-menu-item"
            onClick={() => onOpenChange(false)}
          >
            <UserRound aria-hidden="true" />
            <span>
              <strong>{t("shell.account.profile")}</strong>
              <small>{t("shell.account.profileDescription")}</small>
            </span>
          </NavLink>
          <button type="button" role="menuitem" className="account-menu-item danger" onClick={logout}>
            <LogOut aria-hidden="true" />
            <span>
              <strong>{t("shell.account.logout")}</strong>
              <small>{t("shell.account.logoutDescription")}</small>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
